import { AfterViewInit, Component, EventEmitter, Input, Output } from '@angular/core';
import { ApprovalFlowStep } from '@app/components/approval-status-flow/approval-status-flow.component';
import { ApprovalActions, StepStatus } from '@app/enums';
import { ApprovalInstanceService, AuthService } from '@app/services';
import ChangeRequestFlowStep from '@change-requests/classes/change-request-flow-step.class';
import { ChangeRequestStepStatus } from '@change-requests/enums/change-request-step-status.enum';
import { ApprovalInstanceStep } from '@models/approvals/approval-instance-step.model';
import { ChangeRequest } from '@models/change-requests/change-request.model';
import { Employee } from '@models/employee/employee.model';

@Component({
    selector: 'change-request-approval-instance-flow',
    templateUrl: './change-request-approval-instance-flow.template.html',
    styleUrls: ['./change-request-approval-instance-flow.style.scss'],
})
export class ChangeRequestApprovalInstanceFlowComponent implements AfterViewInit {
    @Input() changeRequest: ChangeRequest;
    @Input() isAdmin = false;
    @Output() approvalActioned: EventEmitter<ApprovalActions> = new EventEmitter<ApprovalActions>();
    stepEmployeeActioned = false;
    hasBeenForcedActioned = false;
    canBeActioned: boolean;
    approvalFlowSteps: ApprovalFlowStep[] = [];
    changeRequestFlowSteps: ChangeRequestFlowStep[] = [];
    approvalActions = ApprovalActions;
    public isCurrentApprover = false;
    public isCancelled = false;
    public isApproved = false;
    private instanceStatusMap = new Map<string, ChangeRequestStepStatus>([
        [StepStatus.Approved, ChangeRequestStepStatus.Approved],
        [StepStatus.Denied, ChangeRequestStepStatus.Denied],
        [StepStatus.Pending, ChangeRequestStepStatus.Pending],
    ]);

    constructor(
        public approvalInstanceService: ApprovalInstanceService,
        private auth: AuthService
    ) {}

    ngAfterViewInit(): void {
        this.approvalFlowSteps = this.getApprovalSteps(this.changeRequest);
        this.isCurrentApprover = this.approvalInstanceService.isCurrentApprover(
            this.changeRequest.approvalInstance,
            this.auth.employee
        );
        this.isCancelled = this.changeRequest.isCancelled();
        this.isApproved = this.changeRequest.approvalInstance.isApproved();
        this.canBeActioned = this.changeRequest.canBeActioned();
    }

    getApprovalSteps(changeRequest: ChangeRequest): ApprovalFlowStep[] {
        let instanceSteps = [...changeRequest.approvalInstance.steps];

        /*
         * Filter out at any pending instance steps if the change request was resolved early
         * e.g. denied, force approved/denied, cancelled
         */
        if (changeRequest.isResolvedEarly()) {
            instanceSteps = this.filterPendingInstanceSteps(instanceSteps);
        }

        let changeRequestFlowSteps = instanceSteps.map(
            (step) =>
                new ChangeRequestFlowStep(
                    step.employee,
                    this.instanceStatusMap.get(step.status),
                    this.auth,
                    step.actionedOn,
                    changeRequest.approvalInstance.closingComments
                )
        );

        changeRequestFlowSteps = this.addSubmittedStep(changeRequest, changeRequestFlowSteps);
        changeRequestFlowSteps = this.addForcedActionStep(changeRequest, changeRequestFlowSteps);
        changeRequestFlowSteps = this.addCancelledStep(changeRequest, changeRequestFlowSteps);
        changeRequestFlowSteps = this.addEffectiveAtStep(changeRequest, changeRequestFlowSteps);

        this.changeRequestFlowSteps = changeRequestFlowSteps;

        return this.covertInstanceStepsToApprovalFlowSteps(this.changeRequestFlowSteps);
    }

    updateApprovalFlow(actor: Employee, updatedChangeRequest: ChangeRequest): void {
        this.stepEmployeeActioned = actor.is(
            this.approvalInstanceService.getCurrentApprover(this.changeRequest.approvalInstance)
        );

        this.hasBeenForcedActioned =
            updatedChangeRequest.approvalInstance.isForcedApproved() ||
            updatedChangeRequest.approvalInstance.isForcedDenied();

        this.approvalFlowSteps = this.getApprovalSteps(updatedChangeRequest);
        this.canBeActioned = updatedChangeRequest.canBeActioned();
    }

    private addSubmittedStep(
        changeRequest: ChangeRequest,
        changeRequestFlowSteps: ChangeRequestFlowStep[]
    ): ChangeRequestFlowStep[] {
        const submittedStep = new ChangeRequestFlowStep(
            changeRequest.requestedBy,
            ChangeRequestStepStatus.Submitted,
            this.auth,
            changeRequest.createdAt
        );

        return [submittedStep, ...changeRequestFlowSteps];
    }

    private addCancelledStep(
        changeRequest: ChangeRequest,
        changeRequestFlowSteps: ChangeRequestFlowStep[]
    ): ChangeRequestFlowStep[] {
        if (changeRequest.isCancelled()) {
            const cancelledStep = new ChangeRequestFlowStep(
                changeRequest.cancelledBy,
                ChangeRequestStepStatus.Cancelled,
                this.auth,
                changeRequest.cancelledAt
            );

            changeRequestFlowSteps = [...changeRequestFlowSteps, cancelledStep];
        }

        return changeRequestFlowSteps;
    }

    private addForcedActionStep(
        changeRequest: ChangeRequest,
        changeRequestFlowSteps: ChangeRequestFlowStep[]
    ): ChangeRequestFlowStep[] {
        if (changeRequest.approvalInstance.isForcedApproved() || changeRequest.approvalInstance.isForcedDenied()) {
            const status = changeRequest.approvalInstance.isForcedApproved()
                ? ChangeRequestStepStatus.ForcedApproved
                : ChangeRequestStepStatus.ForcedDenied;

            const forcedStep = new ChangeRequestFlowStep(
                changeRequest.approvalInstance.forcedBy,
                status,
                this.auth,
                changeRequest.approvalInstance.forcedAt,
                changeRequest.approvalInstance.closingComments ?? ''
            );

            changeRequestFlowSteps = [...changeRequestFlowSteps, forcedStep];
        }

        return changeRequestFlowSteps;
    }

    private addEffectiveAtStep(
        changeRequest: ChangeRequest,
        changeRequestFlowSteps: ChangeRequestFlowStep[]
    ): ChangeRequestFlowStep[] {
        if (
            !changeRequest.isCancelled() &&
            (changeRequest.approvalInstance.isPending() ||
                changeRequest.approvalInstance.isApproved() ||
                changeRequest.approvalInstance.isForcedApproved())
        ) {
            const status = changeRequest.isComplete()
                ? ChangeRequestStepStatus.Completed
                : ChangeRequestStepStatus.EffectiveAt;

            const effectiveStep = new ChangeRequestFlowStep(
                changeRequest.subject,
                status,
                this.auth,
                changeRequest.effectiveAt
            );

            return [...changeRequestFlowSteps, effectiveStep];
        }

        return changeRequestFlowSteps;
    }

    private filterPendingInstanceSteps(approvalSteps: ApprovalInstanceStep[]): ApprovalInstanceStep[] {
        return approvalSteps.filter((step) => step.status !== StepStatus.Pending);
    }

    private covertInstanceStepsToApprovalFlowSteps(
        ChangeRequestFlowSteps: ChangeRequestFlowStep[]
    ): ApprovalFlowStep[] {
        return ChangeRequestFlowSteps.map((step) => {
            return {
                title: step.title,
                description: step.description,
                status: step.componentStatus,
                hideIndicator: false,
            };
        });
    }
}
